import { format, parseISO, startOfDay } from 'date-fns';
import type { Medication, MedicationDose } from '@/types/api';

/**
 * Dose display — the single source of truth for medication dose fanout and
 * derived per-dose state (#846, PRD D-A).
 *
 * Before this module existed, the slot-hour table lived twice (dashboard +
 * calendar) and the dashboard derived TAKEN from `now.getHours()` — a clock
 * comparison reporting adherence the platform did not hold. The rules here
 * are the honest replacement:
 *
 *  - `taken` and `skipped` come ONLY from a stored MedicationDose event.
 *    Nothing in this file ever asserts a dose was taken without a record.
 *  - `pending` / `due` / `missed` are derived from the ABSENCE of a record
 *    against the wall clock. That is the one legitimate use of the clock: it
 *    asserts that nothing was logged, never that something happened.
 *  - Fanout honors the medication's start/end window (the calendar always
 *    did; the dashboard didn't — a med with a future start_date used to show
 *    rows, clock-"taken", before its course even began).
 */

export type DoseSlot = 'morning' | 'midday' | 'evening' | 'bedtime';

/** Slot → representative hour. These are coarse schedule slots, not
 *  prescribed clock times — the Medication model stores no times. */
export const SLOT_HOURS: Record<DoseSlot, number> = {
  morning: 8,
  midday: 12,
  evening: 18,
  bedtime: 22,
};

const FALLBACK_HOUR = 9;

/** How long after the slot hour a dose still reads "due" before the absence
 *  of a log renders as "missed" (D-A). */
export const DOSE_DUE_WINDOW_HOURS = 2;

/**
 * Stored statuses (`taken` | `skipped`) pass through from the dose event.
 * Derived statuses (`pending` | `due` | `missed`) describe an UNLOGGED slot:
 * not reached yet / within the due window / past it.
 */
export type DerivedDoseState = 'taken' | 'skipped' | 'pending' | 'due' | 'missed';

/** Local calendar date key, YYYY-MM-DD. Deliberately not `toISOString()` —
 *  the UTC date can differ from the family's calendar day. */
export function toDateKey(day: Date): string {
  return format(day, 'yyyy-MM-dd');
}

export function formatSlotHour(hour: number): string {
  const h = ((hour + 11) % 12) + 1;
  const mer = hour < 12 ? 'AM' : 'PM';
  return `${h}:00 ${mer}`;
}

export function slotHour(slot: string): number {
  return SLOT_HOURS[slot as DoseSlot] ?? FALLBACK_HOUR;
}

/**
 * Which slots a medication is scheduled for on a given day. Empty when the
 * med is inactive or the day falls outside its optional start/end window —
 * the same window semantics the calendar has always applied
 * (start/end dates are inclusive, compared at start-of-day).
 */
export function medicationSlotsOnDay(med: Medication, day: Date): DoseSlot[] {
  if (!med.active) return [];
  const dayStart = startOfDay(day);
  if (med.start_date && parseISO(med.start_date) > dayStart) return [];
  if (med.end_date && parseISO(med.end_date) < dayStart) return [];
  return med.time_of_day && med.time_of_day.length > 0
    ? (med.time_of_day as DoseSlot[])
    : (['morning'] as DoseSlot[]);
}

/** One scheduled dose slot — a schedule entry, carrying NO adherence claim. */
export interface ScheduledDose {
  /** Natural dose identity, matching the calendar's synthetic event id and
   *  the backend's unique index: med-{medicationId}-{date}-{slot}. */
  key: string;
  medicationId: string;
  date: string;
  slot: DoseSlot;
  hour: number;
  timeLabel: string;
  name: string;
  dose: string;
}

/** Fan the medications out across their slots for one day, sorted by hour. */
export function buildDoseSchedule(meds: Medication[], day: Date): ScheduledDose[] {
  const date = toDateKey(day);
  const out: ScheduledDose[] = [];
  for (const m of meds) {
    for (const slot of medicationSlotsOnDay(m, day)) {
      const hour = slotHour(slot);
      out.push({
        key: `med-${m._id}-${date}-${slot}`,
        medicationId: m._id,
        date,
        slot,
        hour,
        timeLabel: formatSlotHour(hour),
        name: m.medication_name,
        dose: m.dose,
      });
    }
  }
  return out.sort((a, b) => a.hour - b.hour);
}

/** The stored dose event for a (medication, date, slot), if one exists. */
export function findDoseEvent(
  doses: MedicationDose[] | undefined,
  medicationId: string,
  date: string,
  slot: string,
): MedicationDose | undefined {
  return doses?.find(
    (d) => d.medication_id === medicationId && d.date === date && d.slot === slot,
  );
}

/**
 * Derived per-dose state (D-A). Given the dose event (or its absence) and the
 * current time:
 *
 *  - a stored event wins outright — its status IS the state;
 *  - no event, slot hour not reached          → 'pending'
 *  - no event, within DOSE_DUE_WINDOW_HOURS   → 'due'
 *  - no event, at/past the window's end       → 'missed'
 *
 * NEVER stores anything, NEVER returns 'taken' without a record. The clock is
 * only ever used to describe the absence of a log.
 */
export function deriveDoseState(
  date: string,
  slot: DoseSlot,
  doseEvent: MedicationDose | null | undefined,
  now: Date,
): DerivedDoseState {
  if (doseEvent) return doseEvent.status;
  const slotTime = parseISO(date);
  slotTime.setHours(slotHour(slot), 0, 0, 0);
  if (now.getTime() < slotTime.getTime()) return 'pending';
  const windowEnd = slotTime.getTime() + DOSE_DUE_WINDOW_HOURS * 3_600_000;
  if (now.getTime() < windowEnd) return 'due';
  return 'missed';
}
